import React, { FC, useMemo, useState } from "react"
import { LoanOfficer } from "../../../../types"
import {
  LOMap,
  LOMapEntry,
  PricingEngineType
} from "../../../../types/pricing-engine"
import { Trash2, Pencil, Settings2, Info } from "lucide-react"
import LOEntryEditor from "./lo-entry-editor"
import OBProfileModal from "./ob-profile-modal"

interface Props {
  loMap: LOMap
  members: LoanOfficer[]
  onDelete: (loid: string) => void
  onUpdateEntry: (loid: string, entry: LOMapEntry) => void
  engineType: PricingEngineType
  brandId?: string
}

const LOMapTable: FC<Props> = ({
  loMap,
  members,
  onDelete,
  onUpdateEntry,
  engineType,
  brandId
}) => {
  const [editingLoid, setEditingLoid] = useState<string | null>(null)
  const [profileLoid, setProfileLoid] = useState<string | null>(null)

  const isOB = engineType === "optimal_blue"

  const tableData = useMemo(() => {
    return Object.entries(loMap).map(([loid, entry]) => {
      // Match by loid, uid, or nid - compare as strings to handle type mismatches
      const member = members.find(
        m =>
          String(m.loid) === String(loid) ||
          String(m.uid) === String(loid) ||
          String(m.nid) === String(loid)
      )

      const defaultsCount = Object.keys(entry.defaults || {}).length
      const rawDefaultsCount = Object.keys(entry.raw_defaults || {}).length
      const hasOverrides = defaultsCount > 0 || rawDefaultsCount > 0

      return {
        loid,
        entry,
        memberName: member
          ? `${member.first_name} ${member.last_name}`
          : `Unknown (${loid})`,
        memberEmail: member?.email || "",
        originatorId: entry.originator_id,
        bizChannel: entry.biz_channel,
        hasOverrides,
        defaultsCount,
        rawDefaultsCount
      }
    })
  }, [loMap, members])

  if (tableData.length === 0) {
    return (
      <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
        No LO mappings configured. Use the form above to add mappings.
      </div>
    )
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Loan Officer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Originator ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Biz Channel
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Defaults
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.map(row => (
              <React.Fragment key={row.loid}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {row.memberName}
                    </div>
                    <div className="text-xs text-gray-400">{row.loid}</div>
                    <div className="text-sm text-gray-500">
                      {row.memberEmail}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {row.originatorId || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {row.bizChannel || "-"}
                  </td>
                  <td className="px-4 py-3">
                    {row.hasOverrides ? (
                      <div className="flex items-center gap-1.5">
                        <Settings2 size={14} className="text-primary-500" />
                        <span className="text-xs text-gray-600">
                          {row.defaultsCount > 0 && (
                            <span className="text-primary-600">
                              {row.defaultsCount} BB
                            </span>
                          )}
                          {row.defaultsCount > 0 &&
                            row.rawDefaultsCount > 0 &&
                            ", "}
                          {row.rawDefaultsCount > 0 && (
                            <span className="text-amber-600">
                              {row.rawDefaultsCount} Raw
                            </span>
                          )}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isOB && row.bizChannel && row.originatorId && (
                        <button
                          type="button"
                          onClick={() => setProfileLoid(row.loid)}
                          className="text-gray-500 hover:text-blue-600 hover:bg-gray-100 p-1 rounded"
                          title="View OB profile"
                        >
                          <Info size={16} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          setEditingLoid(
                            editingLoid === row.loid ? null : row.loid
                          )
                        }
                        className={`p-1 rounded ${
                          editingLoid === row.loid
                            ? "text-primary-600 bg-primary-50"
                            : "text-gray-500 hover:text-primary-600 hover:bg-gray-100"
                        }`}
                        title="Edit defaults"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(row.loid)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete mapping"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                {/* Inline Editor */}
                {editingLoid === row.loid && loMap[row.loid] && (
                  <tr>
                    <td colSpan={5} className="p-4 bg-gray-50">
                      <LOEntryEditor
                        entry={loMap[row.loid]}
                        onChange={entry => onUpdateEntry(row.loid, entry)}
                        onClose={() => setEditingLoid(null)}
                        engineType={engineType}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {isOB && brandId && profileLoid && loMap[profileLoid] && (
        <OBProfileModal
          open={!!profileLoid}
          onClose={() => setProfileLoid(null)}
          brandId={brandId}
          bizChannel={loMap[profileLoid].biz_channel || ""}
          originatorId={loMap[profileLoid].originator_id || ""}
        />
      )}
    </div>
  )
}

export default LOMapTable
